import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';

import { FaCheck, FaPlus, FaTimes } from 'react-icons/fa';

import classNames from 'classnames';
import AppCard from '../../../../../components/appCard';
import { LargeTablePague } from '../../../../../styles/primeReactConfig';
import { confirmFooter } from '../../../../../components/confirmFooter';
import { useInfo } from '../../../../../context/infoContext';
import { ApiAdimplencia } from '../../../../../services/api';

const DEBOUNCE_MS = 400;

export default function GerenciarRecebiveis({ reload }) {
    const [loading, setIsLoading] = useState(false);
    const [recebivelTipoDescricao, setRecebivelTipoDescricao] = useState('');
    const [recebivelTipos, setRecebivelTipos] = useState([]);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [recebivelTipoData, setRecebivelTipoData] = useState({});
    const [showCreate, setShowCreate] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [type, setType] = useState(false);

    const { credorData } = useInfo();

    const fetchRecebiveis = useCallback(async (pageNum = 0, limit = rows, searchTerm = '') => {
        setIsLoading(true);
        if (!credorData) {
            setIsLoading(false);
            return;
        }
        try {
            const params = new URLSearchParams({ page: pageNum, limit });
            if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
            const { data } = await ApiAdimplencia.get(`/recebivelTipo?${params.toString()}`);
            const dados = data?.dados || data;
            setRecebivelTipos(dados?.results || []);
            setTotalRegistros(dados?.total_registros ?? 0);
        } catch (error) {
            console.error(error?.response);
            toast.error('Houve um erro ao carregar produtos/serviços.');
            setRecebivelTipos([]);
        } finally {
            setIsLoading(false);
        }
    }, [credorData, rows]);

    useEffect(() => {
        fetchRecebiveis(page, rows, search);
    }, [credorData, page, rows, search, reload]);

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput]);

    function onPage(event) {
        setPage(event.page);
        setRows(event.rows);
        setFirst(event.first);
    }

    function atualizaEFecha() {
        setRecebivelTipoData({});
        setRecebivelTipoDescricao('');
        setShowCreate(false);
        setShowUpdate(false);
        setIsDisabled(true);
        setShowConfirm(false);
        fetchRecebiveis(page, rows, search);
    }

    function handleSubmit(event) {
        event.preventDefault();
        setRecebivelTipoData({
            recebivel_tipo_descricao: recebivelTipoDescricao.trim()
        });
        setShowConfirm('C');
    }

    function handleUpdate(event) {
        event.preventDefault();
        if (type === 'U') {
            setRecebivelTipoData({
                recebivel_tipo_descricao: recebivelTipoDescricao.trim(),
                recebivel_tipo_status: showUpdate?.recebivel_tipo_status ?? true,
                recebivel_tipo_id: showUpdate.recebivel_tipo_id
            });
            setShowConfirm('U');
        } else {
            setRecebivelTipoData({ recebivel_tipo_id: showUpdate.recebivel_tipo_id });
            setShowConfirm('D');
        }
    }

    async function cadastraRecebivel() {
        setIsLoading(true);
        try {
            await ApiAdimplencia.post('/recebivelTipo/inserir', recebivelTipoData);
            toast.success('Produto/Serviço cadastrado com sucesso!');
            atualizaEFecha();
        } catch (error) {
            toast.error(error?.response?.data?.mensagem || 'Erro ao cadastrar recebível.');
        } finally {
            setIsLoading(false);
        }
    }

    async function removeRecebivel() {
        setIsLoading(true);
        try {
            await ApiAdimplencia.delete('/recebivelTipo/deletar', { data: recebivelTipoData });
            toast.success('Recebível removido com sucesso!');
            atualizaEFecha();
        } catch (error) {
            if (error?.response?.status === 402) {
                toast.info('Não é possível remover. Este já possui relação com algum contrato.');
            } else {
                toast.error(error?.response?.data?.mensagem || 'Erro ao remover recebível.');
            }
        } finally {
            setIsLoading(false);
        }
    }

    async function atualizaRecebivel() {
        setIsLoading(true);
        try {
            await ApiAdimplencia.put('/recebivelTipo/atualizar', recebivelTipoData);
            toast.success('Recebível atualizado com sucesso!');
            atualizaEFecha();
        } catch (error) {
            toast.error(error?.response?.data?.mensagem || 'Erro ao atualizar recebível.');
        } finally {
            setIsLoading(false);
        }
    }

    const statusTemplate = (row) => (
        <span
            className={classNames(
                'text-nowrap py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center',
                row.recebivel_tipo_status
                    ? 'bg-emerald-500/20 text-emerald-600 dark:bg-emerald-500/30 dark:text-emerald-400'
                    : 'bg-red-500/20 text-red-600 dark:bg-red-500/30 dark:text-red-400'
            )}
        >
            {row.recebivel_tipo_status ? 'Ativo' : 'Inativo'}
        </span>
    );

    return (
        <div className="grid">
            {showConfirm && (
                <ConfirmDialog
                    onHide={(result) => {
                        if (result === 'accept') {
                            setShowCreate(false);
                            setShowUpdate(false);
                        }
                        setShowConfirm(false);
                    }}
                    header={`Deseja ${showConfirm === 'C' ? 'cadastrar' : showConfirm === 'U' ? 'atualizar' : 'remover'} esse recebível?`}
                    message={`${recebivelTipoDescricao}`}
                    acceptClassName="outline-green-500 hover:outline-green-600 outline-2 outline bg-green-500 hover:bg-green-600 rounded-md w-full py-3 text-white transition duration-300"
                    rejectClassName="outline-red-500 outline-2 outline rounded-md w-full py-3 text-red-500 hover:bg-red-500 hover:text-white transition duration-300"
                    acceptLabel="Sim"
                    rejectLabel="Não"
                    acceptIcon={<FaCheck />}
                    rejectIcon={<FaTimes />}
                    accept={() => {
                        if (showConfirm === 'C') cadastraRecebivel();
                        else if (showConfirm === 'U') atualizaRecebivel();
                        else removeRecebivel();
                    }}
                    reject={() => setShowConfirm(false)}
                    pt={{ root: { className: 'lg:w-1/4 md:w-1/2 w-full' } }}
                    footer={confirmFooter}
                    visible
                />
            )}

            {showCreate && (
                <Dialog
                    header="Adicionar novo tipo de produto/serviço"
                    onHide={() => setShowCreate(false)}
                    pt={{ root: { className: 'lg:w-1/3 md:w-1/2 w-full' } }}
                    visible
                >
                    <form onSubmit={(e) => handleSubmit(e)} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <label htmlFor="recebivel_tipo_descricao" className="text-sm font-medium text-gray-700 dark:text-white/80">
                                Descrição
                            </label>
                            <InputText
                                id="recebivel_tipo_descricao"
                                placeholder="Exemplo TAXA EXTRA"
                                value={recebivelTipoDescricao}
                                onChange={(e) => setRecebivelTipoDescricao(e.target.value)}
                                type="text"
                                required
                                className="w-full h-10 min-h-[2.5rem]"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                className="h-10 min-h-[2.5rem] min-w-[6rem]"
                                label="Enviar"
                                type="submit"
                                severity="success"
                            />
                        </div>
                    </form>
                </Dialog>
            )}

            {showUpdate && (
                <Dialog
                    header={`Produto/Serviço: ${showUpdate.recebivel_tipo_descricao || ''}`}
                    pt={{ root: { className: 'lg:w-5/12 md:w-8/12 w-full' } }}
                    onHide={() => {
                        setIsDisabled(true);
                        setRecebivelTipoDescricao('');
                        setShowUpdate(false);
                    }}
                    onShow={() => setRecebivelTipoDescricao(showUpdate.recebivel_tipo_descricao)}
                    visible
                >
                    <form onSubmit={(e) => handleUpdate(e)} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <label htmlFor="recebivel_tipo_descricao" className="text-sm font-medium text-gray-700 dark:text-white/80">
                                Descrição
                            </label>
                            <InputText
                                className="w-full h-10 min-h-[2.5rem]"
                                id="recebivel_tipo_descricao"
                                placeholder="Exemplo TAXA EXTRA"
                                value={recebivelTipoDescricao}
                                onChange={(e) => {
                                    setIsDisabled(false);
                                    setRecebivelTipoDescricao(e.target.value);
                                }}
                                type="text"
                                required
                            />
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button
                                className="h-10 min-h-[2.5rem] min-w-[6rem]"
                                label="Atualizar"
                                disabled={isDisabled}
                                onClick={() => setType('U')}
                                type="submit"
                            />
                            <Button
                                className="h-10 min-h-[2.5rem] min-w-[6rem]"
                                label="Deletar"
                                severity="danger"
                                onClick={() => setType('D')}
                                type="submit"
                            />
                        </div>
                    </form>
                </Dialog>
            )}

            <div className="w-full">
                <AppCard className="!p-0 flex flex-col min-h-[220px]">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-gray-200 dark:border-blue-900/40 shrink-0">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 shrink-0">Produtos / Serviços</h3>
                            <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                                {totalRegistros} {totalRegistros === 1 ? 'item' : 'itens'}
                            </span>
                            <span className="flex-1 min-w-[140px] max-w-[220px]">
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-search text-gray-500 dark:text-gray-400" />
                                    <InputText
                                        placeholder="Buscar por descrição..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className="w-full h-9 text-sm"
                                    />
                                </IconField>
                            </span>
                        </div>
                        <Button
                            loading={loading}
                            icon={<FaPlus className="mr-2" />}
                            label="Adicionar"
                            severity="success"
                            type="button"
                            className="h-10 min-h-[2.5rem] shrink-0"
                            onClick={() => setShowCreate(true)}
                        />
                    </div>
                    <div className="flex-1 overflow-auto min-h-0">
                        <DataTable
                            value={recebivelTipos}
                            loading={loading}
                            paginator
                            lazy
                            first={first}
                            rows={rows}
                            totalRecords={totalRegistros}
                            onPage={onPage}
                            rowsPerPageOptions={[5, 10, 20]}
                            emptyMessage="Nenhum produto/serviço cadastrado."
                            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            pt={{
                                ...LargeTablePague,
                                table: 'table-auto border-collapse min-w-full',
                                header: ({ props }) => ({
                                    className: classNames(
                                        'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-white/80',
                                        'border-b border-gray-200 dark:border-blue-900/40 px-4 py-2.5 text-sm font-medium'
                                    )
                                }),
                                column: {
                                    headercell: { className: 'text-left py-2.5 px-4' },
                                    bodycell: ({ context }) => ({
                                        className: classNames(
                                            'bg-card dark:bg-card_dark',
                                            'text-left border-0 border-b border-solid border-gray-300',
                                            context?.size === 'small' ? 'p-2' : context?.size === 'large' ? 'p-5' : 'py-2.5 px-4',
                                            'dark:text-white/80 dark:border-blue-900/40',
                                            'cursor-pointer',
                                            'transition duration-150',
                                            'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700/80 dark:hover:text-white'
                                        )
                                    })
                                },
                                bodyrow: ({ context }) => ({
                                    className: classNames(
                                        context.selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-300' : 'bg-white text-gray-600 dark:bg-gray-900',
                                        'transition duration-150'
                                    )
                                })
                            }}
                            onSelectionChange={(e) => setShowUpdate(e.value)}
                            selectionMode="single"
                        >
                            <Column field="recebivel_tipo_descricao" header="Descrição" style={{ fontSize: 15, fontWeight: 'bold' }} />
                            <Column field="recebivel_tipo_status" header="Status" body={statusTemplate} style={{ width: '120px' }} />
                        </DataTable>
                    </div>
                </AppCard>
            </div>
        </div>
    );
}
